import { useEffect, useState } from "react"
import { cn } from "@/shared/lib/utils"
import {
	groupOf,
	OTHER_GROUP_ID,
	STORAGE_GROUPS,
	type StorageGroup,
} from "./storage-groups"

const ALL_GROUP_ID = "all"
// Приложение пишет в localStorage без событий, поэтому просто перечитываем.
const POLL_MS = 1000

type Entry = { key: string; value: string; group: string }

type Tab = Pick<StorageGroup, "id" | "label">

// Фичи из ключей `luni:…` получают вкладку по имени.
function tabsFor(entries: Entry[]): Tab[] {
	const known = new Set([...STORAGE_GROUPS.map((g) => g.id), OTHER_GROUP_ID])
	const features = [...new Set(entries.map((e) => e.group))]
		.filter((id) => !known.has(id))
		.map((id) => ({ id, label: id }))
	return [
		{ id: ALL_GROUP_ID, label: "Все" },
		...STORAGE_GROUPS,
		...features,
		{ id: OTHER_GROUP_ID, label: "Прочее" },
	]
}

function readEntries(): Entry[] {
	try {
		return Object.keys(localStorage)
			.sort()
			.map((key) => ({
				key,
				value: localStorage.getItem(key) ?? "",
				group: groupOf(key),
			}))
	} catch {
		return []
	}
}

function sameEntries(a: Entry[], b: Entry[]): boolean {
	return (
		a.length === b.length &&
		a.every((e, i) => e.key === b[i]?.key && e.value === b[i]?.value)
	)
}

function formatValue(value: string): string {
	try {
		return JSON.stringify(JSON.parse(value), null, 2)
	} catch {
		return value
	}
}

export function LocalStoragePanel() {
	const [entries, setEntries] = useState(readEntries)
	const [tab, setTab] = useState(ALL_GROUP_ID)

	useEffect(() => {
		// Без изменений отдаём прежний массив — React пропустит ререндер.
		const id = setInterval(() => {
			const next = readEntries()
			setEntries((prev) => (sameEntries(prev, next) ? prev : next))
		}, POLL_MS)
		return () => clearInterval(id)
	}, [])

	const tabs = tabsFor(entries)
	const visible =
		tab === ALL_GROUP_ID ? entries : entries.filter((e) => e.group === tab)

	const remove = (keys: string[]) => {
		for (const key of keys) localStorage.removeItem(key)
		setEntries(readEntries())
	}

	const clearTab = () => {
		const label = tabs.find((t) => t.id === tab)?.label
		const warnings = STORAGE_GROUPS.filter(
			(group) => group.warning && visible.some((e) => e.group === group.id),
		).map((group) => `\n\n⚠️ ${group.label}: ${group.warning}`)
		const message = `Удалить ${visible.length} ключей из «${label}»?${warnings.join("")}`
		if (!window.confirm(message)) return
		remove(visible.map((e) => e.key))
	}

	return (
		// Скроллит контейнер плагина в Devtools — своих overflow-обёрток не
		// заводим: по канону TanStack Devtools вложенный скроллер становится
		// тупиком, и на тач-экране панель перестаёт листаться.
		// Свой фон, чтобы панель одинаково читалась в светлой и тёмной теме Devtools.
		<div className="min-h-full bg-neutral-900 font-mono text-[12px] text-neutral-200 select-text">
			<div className="sticky top-0 z-10 flex flex-col gap-3 bg-neutral-900 p-3">
				<div className="flex flex-wrap gap-1.5">
					{tabs.map(({ id, label }) => {
						const count =
							id === ALL_GROUP_ID
								? entries.length
								: entries.filter((e) => e.group === id).length
						return (
							<button
								key={id}
								type="button"
								onClick={() => setTab(id)}
								className={cn(
									"rounded-md px-2 py-1",
									tab === id
										? "bg-neutral-200 text-neutral-900"
										: "bg-neutral-800 hover:bg-neutral-700",
									count === 0 && tab !== id && "opacity-40",
								)}
							>
								{label} · {count}
							</button>
						)
					})}
				</div>

				<div className="flex gap-1.5">
					<button
						type="button"
						disabled={visible.length === 0}
						onClick={clearTab}
						className="rounded-md bg-red-900 px-2 py-1 hover:bg-red-800 disabled:opacity-40"
					>
						Очистить вкладку
					</button>
					{/* Виджеты читают localStorage при монтировании — без перезагрузки
					    очищенное состояние на экране не появится. */}
					<button
						type="button"
						onClick={() => window.location.reload()}
						className="rounded-md bg-neutral-800 px-2 py-1 hover:bg-neutral-700"
					>
						Перезагрузить приложение
					</button>
				</div>
			</div>

			<ul className="divide-y divide-neutral-800 px-3 pb-3">
				{visible.length === 0 && (
					<li className="py-2 text-neutral-500">Пусто</li>
				)}
				{visible.map(({ key, value }) => (
					<EntryRow key={key} entryKey={key} value={value} onRemove={remove} />
				))}
			</ul>
		</div>
	)
}

interface EntryRowProps {
	entryKey: string
	value: string
	onRemove: (keys: string[]) => void
}

// Форматируем значение только у раскрытой строки: опрос перерисовывает
// панель раз в секунду, а в PostHog-ключах лежат крупные JSON.
function EntryRow({ entryKey, value, onRemove }: EntryRowProps) {
	const [open, setOpen] = useState(false)

	return (
		<li className="flex items-start gap-2 py-1.5">
			<details
				className="min-w-0 flex-1"
				onToggle={(event) => setOpen(event.currentTarget.open)}
			>
				<summary className="cursor-pointer truncate">
					<span className="text-sky-300">{entryKey}</span>
					<span className="text-neutral-500"> = {value}</span>
				</summary>
				{open && (
					<pre className="mt-1 break-all whitespace-pre-wrap text-neutral-300">
						{formatValue(value)}
					</pre>
				)}
			</details>
			<button
				type="button"
				onClick={() => onRemove([entryKey])}
				className="shrink-0 rounded px-1.5 text-neutral-400 hover:bg-neutral-800 hover:text-red-400"
				aria-label={`Удалить ${entryKey}`}
			>
				✕
			</button>
		</li>
	)
}
